import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { CalendarDays, Check, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Switch } from '../ui/Switch';
import { useMediaQuery } from '../campaign-editor/hooks';
import { cn } from '../../lib/utils';
import {
  PRESETS,
  addDays,
  compareRange,
  earliestDay,
  formatDayLong,
  formatMonth,
  formatRange,
  formatRangeShort,
  fromKey,
  matchPreset,
  monthFromIndex,
  monthIndex,
  monthWeeks,
  orderRange,
  presetRange,
  shiftMonths,
  todayKey,
  type CompareMode,
  type DayKey,
  type DayRange,
  type PresetId,
} from './dateRange';

export interface PeriodChoice {
  range: DayRange;
  compare: CompareMode | 'off';
  customCompare: DayRange | null;
}

/** The compare range a choice stands for, or null when compare is off. */
export function choiceCompareRange(choice: PeriodChoice): DayRange | null {
  if (choice.compare === 'off') return null;
  return compareRange(choice.range, choice.compare, choice.customCompare);
}

interface Draft {
  range: DayRange;
  cmpOn: boolean;
  cmpMode: CompareMode;
  custom: DayRange | null;
  /** Which range the next calendar clicks set. */
  target: 'main' | 'cmp';
  /** 'end' = the first day is chosen and the next click ends the range. */
  picking: 'start' | 'end';
  /** First day clicked while `picking === 'end'`. */
  anchor: DayKey | null;
  hover: DayKey | null;
  /** "Custom range" was clicked: it stays marked until a day or a preset is clicked. */
  customPick: boolean;
}

const COMPARE_OPTIONS: { value: CompareMode; label: string }[] = [
  { value: 'prev', label: 'previous period' },
  { value: 'month', label: 'same days, month before' },
  { value: 'custom', label: 'custom range' },
];

const WEEKDAYS = [
  ['Mo', 'Monday'],
  ['Tu', 'Tuesday'],
  ['We', 'Wednesday'],
  ['Th', 'Thursday'],
  ['Fr', 'Friday'],
  ['Sa', 'Saturday'],
  ['Su', 'Sunday'],
] as const;

/** Same width as Tailwind's `sm` breakpoint: below it the picker shows one month. */
const NARROW_QUERY = '(max-width: 639.98px)';
/** Space kept between the popover and the viewport edge. */
const GUTTER = 16;

const pad2 = (n: number) => String(n).padStart(2, '0');
function monthId(index: number): string {
  const { year, month } = monthFromIndex(index);
  return `${year}-${pad2(month + 1)}`;
}

function draftFrom(value: PeriodChoice): Draft {
  return {
    range: value.range,
    cmpOn: value.compare !== 'off',
    cmpMode: value.compare === 'off' ? 'prev' : value.compare,
    custom: value.customCompare,
    target: 'main',
    picking: 'start',
    anchor: null,
    hover: null,
    customPick: false,
  };
}

function draftCompare(d: Draft): DayRange | null {
  if (!d.cmpOn) return null;
  if (d.cmpMode === 'custom') return d.custom;
  return compareRange(d.range, d.cmpMode, null);
}

/**
 * Period picker: quick ranges on the left, two months side by side (one on a
 * phone), and in the footer an optional compare range and Cancel/Apply.
 * Nothing changes until Apply.
 */
export function DateRangePicker({
  value,
  onApply,
  today = todayKey(),
}: {
  value: PeriodChoice;
  onApply: (next: PeriodChoice) => void;
  today?: DayKey;
}) {
  const narrow = useMediaQuery(NARROW_QUERY);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [viewEnd, setViewEnd] = useState(() => monthIndex(value.range.end));
  const [slide, setSlide] = useState<'next' | 'prev' | null>(null);
  const [focusDay, setFocusDay] = useState<DayKey>(value.range.end);
  const [shift, setShift] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const calRef = useRef<HTMLDivElement>(null);
  const presetsRef = useRef<HTMLDivElement>(null);
  /** Set when the roving day should get focus after the next render. */
  const focusPending = useRef(false);
  /** Set after a month arrow is used: the calendar remounts, so focus goes back to that arrow. */
  const arrowFocus = useRef<'Previous month' | 'Next month' | null>(null);
  const open = draft !== null;

  const firstDay = earliestDay(today);
  const firstMonth = monthIndex(firstDay);
  const lastMonth = monthIndex(today);
  const span = narrow ? 1 : 2;
  const clampView = (m: number) => Math.min(lastMonth, Math.max(firstMonth + span - 1, m));
  const end = clampView(viewEnd);
  const shownMonths = span === 2 ? [end - 1, end] : [end];
  const firstShown = shownMonths[0] ?? end;
  const isShown = (day: DayKey) => {
    const m = monthIndex(day);
    return m >= firstShown && m <= end;
  };
  const tabDay = isShown(focusDay) ? focusDay : `${monthId(firstShown)}-01`;

  const close = useCallback((returnFocus: boolean) => {
    setDraft(null);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  function showMonth(target: number) {
    const next = clampView(target);
    if (next === end) return;
    setSlide(next > end ? 'next' : 'prev');
    setViewEnd(next);
  }

  /** Moves the view the least needed for `day` to be on screen. */
  function bringIntoView(day: DayKey) {
    const m = monthIndex(day);
    if (m > end) showMonth(m);
    else if (m < firstShown) showMonth(m + span - 1);
  }

  function openPicker() {
    const endDay = value.range.end < firstDay ? today : value.range.end;
    setSlide(null);
    setViewEnd(clampView(monthIndex(endDay)));
    setFocusDay(endDay);
    setDraft(draftFrom(value));
    focusPending.current = true;
  }

  // Keep the popover inside the viewport (16px from each edge).
  const place = useCallback(() => {
    const pop = popRef.current;
    const wrap = wrapRef.current;
    if (!pop || !wrap) return;
    const vw = document.documentElement.clientWidth;
    const left = wrap.getBoundingClientRect().left;
    const want = Math.max(GUTTER, Math.min(left, vw - GUTTER - pop.offsetWidth));
    setShift(want - left);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open, place]);

  // Focus the roving day when asked (on open, after keyboard moves, after "Custom range").
  useEffect(() => {
    if (open && arrowFocus.current) {
      const label = arrowFocus.current;
      const other = label === 'Next month' ? 'Previous month' : 'Next month';
      arrowFocus.current = null;
      const cal = calRef.current;
      const arrow =
        cal?.querySelector<HTMLButtonElement>(`button[aria-label="${label}"]:not(:disabled)`) ??
        cal?.querySelector<HTMLButtonElement>(`button[aria-label="${other}"]:not(:disabled)`) ??
        cal?.querySelector<HTMLButtonElement>('[data-day][tabindex="0"]');
      arrow?.focus();
      return;
    }
    if (!open || !focusPending.current) return;
    focusPending.current = false;
    calRef.current?.querySelector<HTMLButtonElement>('[data-day][tabindex="0"]')?.focus();
  });

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(true);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const update = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  function clickDay(day: DayKey) {
    if (!draft) return;
    setFocusDay(day);
    const done = { anchor: null, picking: 'start' as const, hover: null };
    if (draft.target === 'cmp') {
      if (draft.picking === 'start' || !draft.anchor) {
        update({ custom: { start: day, end: day }, anchor: day, picking: 'end', hover: null });
      } else {
        update({ ...done, custom: orderRange(draft.anchor, day), target: 'main' });
      }
      return;
    }
    if (draft.picking === 'start' || !draft.anchor) {
      update({
        range: { start: day, end: day },
        anchor: day,
        picking: 'end',
        hover: null,
        customPick: false,
      });
    } else {
      update({ ...done, range: orderRange(draft.anchor, day), customPick: false });
    }
  }

  function choosePreset(id: PresetId) {
    const range = presetRange(id, today);
    update({
      range,
      picking: 'start',
      target: 'main',
      anchor: null,
      hover: null,
      customPick: false,
    });
    setFocusDay(range.end);
    showMonth(monthIndex(range.end));
  }

  function chooseCustom() {
    update({ customPick: true, target: 'main', picking: 'start', anchor: null, hover: null });
    focusPending.current = true;
  }

  function toggleCompare() {
    if (!draft) return;
    if (draft.cmpOn) {
      update(
        draft.target === 'cmp'
          ? { cmpOn: false, target: 'main', picking: 'start', anchor: null, hover: null }
          : { cmpOn: false },
      );
    } else if (draft.cmpMode === 'custom' && !draft.custom) {
      update({ cmpOn: true, target: 'cmp', picking: 'start', anchor: null, hover: null });
    } else {
      update({ cmpOn: true });
    }
  }

  function chooseMode(mode: CompareMode) {
    update({
      cmpMode: mode,
      target: mode === 'custom' ? 'cmp' : 'main',
      picking: 'start',
      anchor: null,
      hover: null,
    });
  }

  function apply() {
    if (!draft) return;
    const range =
      draft.target === 'main' && draft.picking === 'end' && draft.anchor
        ? { start: draft.anchor, end: draft.anchor }
        : draft.range;
    const custom = draft.cmpMode === 'custom' ? draft.custom : null;
    const compare: PeriodChoice['compare'] = !draft.cmpOn
      ? 'off'
      : draft.cmpMode === 'custom' && !custom
        ? 'prev'
        : draft.cmpMode;
    onApply({ range, compare, customCompare: compare === 'custom' ? custom : null });
    close(true);
  }

  function onCalendarKey(e: KeyboardEvent<HTMLDivElement>) {
    const from = (e.target as HTMLElement).dataset.day;
    if (!from) return;
    const weekday = (fromKey(from).getDay() + 6) % 7;
    const step = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
      Home: -weekday,
      End: 6 - weekday,
    }[e.key];
    let next: DayKey;
    if (step !== undefined) next = addDays(from, step);
    else if (e.key === 'PageUp') next = shiftMonths(from, -1);
    else if (e.key === 'PageDown') next = shiftMonths(from, 1);
    else return;
    e.preventDefault();
    if (next > today) next = today;
    if (next < firstDay) next = firstDay;
    setFocusDay(next);
    bringIntoView(next);
    if (draft?.picking === 'end') update({ hover: next });
    focusPending.current = true;
  }

  // What the calendar paints.
  let lo: DayKey | null = draft?.range.start ?? null;
  let hi: DayKey | null = draft?.range.end ?? null;
  if (draft && draft.target === 'main' && draft.picking === 'end' && draft.anchor) {
    const r = orderRange(draft.anchor, draft.hover ?? draft.anchor);
    lo = r.start;
    hi = r.end;
  }
  let cmpPaint = draft ? draftCompare(draft) : null;
  if (draft && draft.target === 'cmp' && draft.picking === 'end' && draft.anchor) {
    cmpPaint = orderRange(draft.anchor, draft.hover ?? draft.anchor);
  }
  const previewing = !!draft && draft.picking === 'end';
  const mainPicking = !!draft && draft.target === 'main' && draft.picking === 'end';
  const activePreset =
    draft && !draft.customPick && !mainPicking ? matchPreset(draft.range, today) : null;
  const activeKey = draft ? (activePreset ?? 'custom') : null;
  const shownCompare = choiceCompareRange(value);
  const draftCmp = draft ? draftCompare(draft) : null;
  const hint = !draft
    ? ''
    : draft.target === 'cmp'
      ? draft.picking === 'end'
        ? 'Now click the last day to compare with.'
        : 'Click the first day to compare with.'
      : draft.picking === 'end'
        ? 'Now click the end date.'
        : draft.customPick
          ? 'Click the first day, then the last day.'
          : '';

  // On a phone the quick ranges are one scrolling row: keep the marked one in sight.
  useEffect(() => {
    const row = presetsRef.current;
    if (!row || !narrow) return;
    const btn = row.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!btn) return;
    const pad = 16;
    if (btn.offsetLeft - pad < row.scrollLeft) row.scrollLeft = btn.offsetLeft - pad;
    else if (btn.offsetLeft + btn.offsetWidth + pad > row.scrollLeft + row.clientWidth) {
      row.scrollLeft = btn.offsetLeft + btn.offsetWidth + pad - row.clientWidth;
    }
  }, [activeKey, narrow]);

  return (
    <div ref={wrapRef} className="relative max-sm:w-full">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Period: ${formatRange(value.range)}${shownCompare ? `, compared with ${formatRange(shownCompare)}` : ''}. Change period`}
        onClick={() => (open ? close(false) : openPicker())}
        className={cn(
          'group flex min-w-[260px] max-w-full items-center gap-2.5 rounded-[12px] border border-border bg-card py-1.5 pr-3 pl-1.5 text-left text-sm',
          'shadow-[0_1px_2px_rgb(0_0_0/0.04)] transition-[border-color,box-shadow] duration-200 hover:border-zinc-300',
          'aria-expanded:border-zinc-300 aria-expanded:shadow-[0_0_0_4px_rgb(0_0_0/0.04)] max-sm:w-full max-sm:min-w-0',
        )}
      >
        <span
          aria-hidden="true"
          className="grid h-8 w-8 flex-none place-items-center rounded-[9px] bg-accent-soft text-accent"
        >
          <CalendarDays size={16} />
        </span>
        <span className="flex min-w-0 flex-col leading-tight">
          <b className="truncate font-medium">{formatRange(value.range)}</b>
          {shownCompare ? (
            <small className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-muted">
              <CompareSwatch />
              <span className="truncate">vs {formatRange(shownCompare)}</span>
            </small>
          ) : null}
        </span>
        <ChevronDown
          size={16}
          aria-hidden="true"
          className="ml-auto flex-none text-muted transition-transform duration-200 group-aria-expanded:rotate-180"
        />
      </button>

      {draft ? (
        <div
          ref={popRef}
          role="dialog"
          aria-label="Choose period"
          style={{ left: shift }}
          className={cn(
            'absolute top-[calc(100%+8px)] left-0 z-30 w-[min(780px,calc(100vw-32px))] origin-top-left animate-picker-in overflow-hidden',
            'rounded-[24px] border border-black/[0.06] bg-[#fafafa]',
            'shadow-[0_24px_64px_-16px_rgb(0_0_0/0.22),0_6px_16px_-6px_rgb(0_0_0/0.08)]',
          )}
        >
          <div className="flex max-sm:flex-col">
            <div
              ref={presetsRef}
              role="group"
              aria-label="Quick ranges"
              className={cn(
                'relative flex w-[172px] flex-none flex-col gap-0.5 border-r border-black/[0.07] p-2',
                'max-sm:w-auto max-sm:flex-row max-sm:gap-1.5 max-sm:overflow-x-auto max-sm:border-r-0 max-sm:px-3 max-sm:pt-3 max-sm:pb-1 max-sm:[scrollbar-width:none]',
                'max-sm:[mask-image:linear-gradient(90deg,transparent,#000_14px,#000_calc(100%-14px),transparent)]',
              )}
            >
              {PRESETS.map((p) => (
                <PresetButton
                  key={p.id}
                  label={p.label}
                  active={activePreset === p.id}
                  onClick={() => choosePreset(p.id)}
                />
              ))}
              <PresetButton label="Custom range" active={!activePreset} onClick={chooseCustom} />
            </div>

            <div className="min-w-0 flex-1 px-5 pt-4 pb-2 max-sm:px-3 max-sm:pt-2">
              <div
                ref={calRef}
                onKeyDown={onCalendarKey}
                onMouseLeave={() => {
                  if (draft.hover) update({ hover: null });
                }}
                key={end}
                className={cn(
                  'grid gap-8',
                  span === 2 ? 'grid-cols-2' : 'grid-cols-1',
                  slide === 'next' && 'animate-month-next',
                  slide === 'prev' && 'animate-month-prev',
                )}
              >
                {shownMonths.map((m, i) => (
                  <MonthView
                    key={m}
                    index={m}
                    today={today}
                    lo={lo}
                    hi={hi}
                    cmp={cmpPaint}
                    tabDay={tabDay}
                    fast={previewing}
                    prev={
                      i === 0
                        ? {
                            disabled: end - span + 1 <= firstMonth,
                            onClick: () => {
                              arrowFocus.current = 'Previous month';
                              showMonth(end - 1);
                            },
                          }
                        : null
                    }
                    next={
                      i === shownMonths.length - 1
                        ? {
                            disabled: end >= lastMonth,
                            onClick: () => {
                              arrowFocus.current = 'Next month';
                              showMonth(end + 1);
                            },
                          }
                        : null
                    }
                    onDay={clickDay}
                    onHover={(day) => {
                      if (draft.picking === 'end' && draft.hover !== day) update({ hover: day });
                    }}
                  />
                ))}
              </div>
              <p
                className={cn(
                  'm-0 min-h-5 pt-1 text-xs',
                  draft.target === 'cmp' ? 'text-warning-text' : 'text-muted',
                )}
                aria-live="polite"
              >
                {hint}
              </p>
            </div>
          </div>

          <div
            className={cn(
              'flex items-center gap-x-4 gap-y-3 border-t border-black/[0.07] px-5 py-3.5',
              'max-sm:flex-col max-sm:items-stretch max-sm:px-4',
            )}
          >
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              <Switch checked={draft.cmpOn} label="Compare" onChange={toggleCompare} />
              <span aria-hidden="true" className={cn('ml-1', !draft.cmpOn && 'text-muted')}>
                Compare to
              </span>
              <ModeMenu value={draft.cmpMode} disabled={!draft.cmpOn} onChange={chooseMode} />
              {draft.cmpOn && draftCmp ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-muted tabular-nums">
                  <CompareSwatch />
                  {draft.cmpMode === 'custom' ? (
                    <button
                      type="button"
                      onClick={() =>
                        update({ target: 'cmp', picking: 'start', anchor: null, hover: null })
                      }
                      aria-label={`Compared with ${formatRange(draftCmp)}. Pick the compare range again`}
                      className="rounded-sm underline decoration-dotted underline-offset-2 hover:text-text"
                    >
                      {formatRangeShort(draftCmp, today)}
                    </button>
                  ) : (
                    formatRangeShort(draftCmp, today)
                  )}
                </span>
              ) : null}
            </div>
            <div className="ml-auto flex gap-2 max-sm:ml-0 max-sm:grid max-sm:grid-cols-2">
              <button
                type="button"
                onClick={() => close(true)}
                className="h-9 rounded-[12px] bg-black/[0.055] px-4 text-sm font-medium text-text transition-colors duration-150 hover:bg-black/[0.09]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={apply}
                className={cn(
                  'h-9 rounded-[12px] bg-[linear-gradient(180deg,#3f3f46,#18181b)] px-4 text-sm font-medium text-white',
                  'shadow-[0_6px_14px_-4px_rgb(24_24_27/0.45),inset_0_1px_0_rgb(255_255_255/0.12)] transition-[filter] duration-150 hover:brightness-125',
                )}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** The dashed amber mark the compare range has in the calendar, as a small legend. */
function CompareSwatch() {
  return (
    <i
      aria-hidden="true"
      className="inline-block h-2.5 w-4 flex-none rounded-full border-[1.5px] border-dashed border-amber-600 bg-amber-50"
    />
  );
}

function PresetButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'flex items-center justify-between gap-3 rounded-[12px] px-3 py-2.5 text-left text-sm text-text',
        'transition-[background-color,box-shadow] duration-200 ease-out hover:bg-black/[0.035]',
        'max-sm:flex-none max-sm:whitespace-nowrap max-sm:rounded-full max-sm:bg-black/[0.04] max-sm:px-3.5 max-sm:py-1.5 max-sm:text-[13px]',
        active &&
          'bg-card shadow-[0_1px_2px_rgb(0_0_0/0.06),0_2px_8px_rgb(0_0_0/0.05)] hover:bg-card max-sm:bg-card',
      )}
    >
      <span>{label}</span>
      <Check
        size={16}
        strokeWidth={2.25}
        aria-hidden="true"
        className={cn(
          'flex-none transition-opacity duration-200 max-sm:hidden',
          active ? 'opacity-100' : 'opacity-0',
        )}
      />
    </button>
  );
}

/** The compare mode as a word in the sentence; a native select laid over it does the work. */
function ModeMenu({
  value,
  disabled,
  onChange,
}: {
  value: CompareMode;
  disabled: boolean;
  onChange: (mode: CompareMode) => void;
}) {
  const label = COMPARE_OPTIONS.find((o) => o.value === value)?.label ?? '';
  return (
    <span
      className={cn(
        'relative inline-flex items-center gap-0.5 rounded-md font-medium',
        'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent-hover',
        disabled ? 'text-muted' : 'hover:text-link',
      )}
    >
      <span aria-hidden="true">{label}</span>
      <ChevronDown size={14} aria-hidden="true" className="text-muted" />
      <select
        aria-label="Compare with"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as CompareMode)}
        className="absolute inset-0 cursor-pointer appearance-none opacity-0 outline-none disabled:cursor-default"
      >
        {COMPARE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </span>
  );
}

type Segment = { from: number; to: number } | 'before' | 'after' | null;

/** Which columns of a week a range covers, or which side of the week it lies on. */
function weekSegment(week: (DayKey | null)[], lo: DayKey | null, hi: DayKey | null): Segment {
  if (!lo || !hi) return null;
  const cols = week.flatMap((d, i) => (d && d >= lo && d <= hi ? [i] : []));
  const first = cols[0];
  const last = cols.at(-1);
  if (first !== undefined && last !== undefined) return { from: first, to: last };
  const firstDay = week.find((d) => d !== null);
  return firstDay && firstDay > hi ? 'after' : 'before';
}

/**
 * Position of a band (one pill per week row). It runs from the left edge of
 * the first day's circle to the right edge of the last one. A week outside
 * the range keeps a zero-width band on the side the range is, so a new range
 * grows out of that side instead of popping in.
 */
function bandStyle(seg: Segment): CSSProperties {
  if (seg === null) return { left: 0, width: 0, opacity: 0 };
  if (seg === 'before') return { left: '100%', width: 0, opacity: 0 };
  if (seg === 'after') return { left: 0, width: 0, opacity: 0 };
  const n = seg.to - seg.from + 1;
  return {
    left: `calc(${seg.from} * 100% / 7 + (100% / 7 - 2rem) / 2)`,
    width: `calc(${n} * 100% / 7 - (100% / 7 - 2rem))`,
    opacity: 1,
  };
}

function MonthView({
  index,
  today,
  lo,
  hi,
  cmp,
  tabDay,
  fast,
  prev,
  next,
  onDay,
  onHover,
}: {
  index: number;
  today: DayKey;
  lo: DayKey | null;
  hi: DayKey | null;
  cmp: DayRange | null;
  tabDay: DayKey;
  /** Shorter transitions while the band follows the mouse. */
  fast: boolean;
  prev: { disabled: boolean; onClick: () => void } | null;
  next: { disabled: boolean; onClick: () => void } | null;
  onDay: (day: DayKey) => void;
  onHover: (day: DayKey) => void;
}) {
  const { year, month } = monthFromIndex(index);
  const title = formatMonth(year, month);
  const weeks = monthWeeks(year, month);
  const band = cn(
    'pointer-events-none absolute top-1/2 h-8 -translate-y-1/2 rounded-full ease-out',
    'transition-[left,width,opacity]',
    fast ? 'duration-100' : 'duration-[240ms]',
  );
  return (
    <div role="group" aria-label={title} className="min-w-0">
      <div className="mb-3 grid grid-cols-[28px_1fr_28px] items-center">
        {prev ? (
          <ArrowButton label="Previous month" {...prev}>
            <ChevronLeft size={18} />
          </ArrowButton>
        ) : (
          <span />
        )}
        <div className="text-center text-[15px] font-semibold tracking-[-0.01em]">{title}</div>
        {next ? (
          <ArrowButton label="Next month" {...next}>
            <ChevronRight size={18} />
          </ArrowButton>
        ) : (
          <span />
        )}
      </div>
      <div className="mb-1 grid grid-cols-7" aria-hidden="true">
        {WEEKDAYS.map(([short, long]) => (
          <abbr key={long} title={long} className="text-center text-[11px] text-faint no-underline">
            {short}
          </abbr>
        ))}
      </div>
      <div className="min-h-[216px]">
        {weeks.map((week, w) => (
          <div key={w} className="relative grid grid-cols-7">
            <span
              aria-hidden="true"
              data-band="main"
              className={cn(band, 'bg-[color-mix(in_srgb,var(--color-accent)_13%,white)]')}
              style={bandStyle(weekSegment(week, lo, hi))}
            />
            <span
              aria-hidden="true"
              data-band="compare"
              className={cn(band, 'border-[1.5px] border-dashed border-amber-600 bg-amber-50/60')}
              style={bandStyle(cmp ? weekSegment(week, cmp.start, cmp.end) : null)}
            />
            {week.map((day, i) =>
              day === null ? (
                <span key={`blank-${i}`} className="h-9" />
              ) : (
                <div key={day} className="grid h-9 place-items-center">
                  <DayButton
                    day={day}
                    disabled={day > today}
                    isToday={day === today}
                    inRange={!!lo && !!hi && day >= lo && day <= hi}
                    edge={day === lo || day === hi}
                    tabbable={day === tabDay}
                    onClick={() => onDay(day)}
                    onHover={() => onHover(day)}
                  />
                </div>
              ),
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ArrowButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="grid h-7 w-7 place-items-center rounded-full text-text transition-colors duration-150 hover:bg-black/[0.06] disabled:text-zinc-300 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}

function DayButton({
  day,
  disabled,
  isToday,
  inRange,
  edge,
  tabbable,
  onClick,
  onHover,
}: {
  day: DayKey;
  disabled: boolean;
  isToday: boolean;
  inRange: boolean;
  edge: boolean;
  tabbable: boolean;
  onClick: () => void;
  onHover: () => void;
}) {
  return (
    <button
      type="button"
      data-day={day}
      tabIndex={tabbable ? 0 : -1}
      disabled={disabled}
      aria-label={`${formatDayLong(day)}${isToday ? ', today' : ''}`}
      aria-pressed={inRange}
      onClick={onClick}
      onMouseEnter={onHover}
      className={cn(
        'group relative grid h-8 w-8 place-items-center rounded-full text-[13px] tabular-nums',
        !disabled && !edge && 'hover:bg-black/[0.05]',
        disabled && 'cursor-default',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'absolute inset-0 rounded-full bg-accent shadow-[0_2px_6px_-1px_rgb(84_87_214/0.45)]',
          'transition-[opacity,transform] duration-200 ease-out',
          edge ? 'scale-100 opacity-100' : 'scale-75 opacity-0',
        )}
      />
      <span
        className={cn(
          'relative transition-colors duration-200',
          edge ? 'font-medium text-white' : disabled ? 'text-zinc-400' : 'text-text',
          isToday && !edge && 'font-semibold',
        )}
      >
        {Number(day.slice(8))}
      </span>
      {isToday ? (
        <span
          aria-hidden="true"
          className={cn(
            'absolute bottom-[3px] h-1 w-1 rounded-full',
            edge ? 'bg-white' : 'bg-accent',
          )}
        />
      ) : null}
    </button>
  );
}
